/* ============================================================
   POUFUDIC Mega Menu 2026 — Interaction Layer
   - Desktop: hover intent + ARIA disclosure
   - Mobile: drill-down drawer + body scroll lock + focus trap
   ============================================================ */
(function () {
  "use strict";

  if (window.__pfMegaMenuLoaded__) return;
  window.__pfMegaMenuLoaded__ = true;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const HOVER_OPEN_DELAY = prefersReducedMotion ? 0 : 120;
  const HOVER_CLOSE_DELAY = prefersReducedMotion ? 0 : 280;

  /* ---------- Desktop Mega Menu ---------- */
  class PFMegaMenu extends HTMLElement {
    constructor() {
      super();
      this._openItem = null;
      this._openTimer = null;
      this._closeTimer = null;
      this._onPointerEnter = this._onPointerEnter.bind(this);
      this._onPointerLeave = this._onPointerLeave.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
    }

    connectedCallback() {
      this._items = Array.from(this.querySelectorAll('[data-pf-mm-item]'));
      this._items.forEach((item) => {
        const trigger = item.querySelector('[data-pf-mm-trigger]');
        const panel = item.querySelector('[data-pf-mm-panel]');
        if (!trigger || !panel) return;

        item.addEventListener("pointerenter", this._onPointerEnter);
        item.addEventListener("pointerleave", this._onPointerLeave);
        item.addEventListener("focusin", this._onFocusIn);
        item.addEventListener("focusout", this._onFocusOut);
        trigger.addEventListener("click", this._onTriggerClick);
        trigger.addEventListener("keydown", this._onKeyDown);
      });

      document.addEventListener("click", this._onDocClick);
      document.addEventListener("keydown", this._onKeyDown);
    }

    disconnectedCallback() {
      document.removeEventListener("click", this._onDocClick);
      document.removeEventListener("keydown", this._onKeyDown);
      this._clearTimers();
    }

    _clearTimers() {
      if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null; }
      if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
    }

    _open(item) {
      if (this._openItem && this._openItem !== item) this._close(this._openItem, true);
      const trigger = item.querySelector('[data-pf-mm-trigger]');
      const panel = item.querySelector('[data-pf-mm-panel]');
      if (!trigger || !panel) return;

      item.setAttribute("data-pf-mm-active", "true");
      trigger.setAttribute("aria-expanded", "true");
      panel.setAttribute("data-pf-mm-open", "true");
      panel.removeAttribute("hidden");
      this._openItem = item;
    }

    _close(item, immediate) {
      if (!item) return;
      const trigger = item.querySelector('[data-pf-mm-trigger]');
      const panel = item.querySelector('[data-pf-mm-panel]');
      item.removeAttribute("data-pf-mm-active");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (panel) {
        panel.setAttribute("data-pf-mm-open", "false");
        if (immediate) panel.setAttribute("hidden", "");
        else setTimeout(() => { if (panel.getAttribute("data-pf-mm-open") !== "true") panel.setAttribute("hidden", ""); }, 240);
      }
      if (this._openItem === item) this._openItem = null;
    }

    _closeAll(immediate) {
      this._items.forEach((it) => this._close(it, immediate));
    }

    _onPointerEnter(e) {
      const item = e.currentTarget;
      this._clearTimers();
      this._openTimer = setTimeout(() => this._open(item), HOVER_OPEN_DELAY);
    }

    _onPointerLeave(e) {
      const item = e.currentTarget;
      this._clearTimers();
      this._closeTimer = setTimeout(() => this._close(item), HOVER_CLOSE_DELAY);
    }

    _onFocusIn(e) {
      const item = e.currentTarget;
      this._clearTimers();
      this._open(item);
    }

    _onFocusOut(e) {
      const item = e.currentTarget;
      const next = e.relatedTarget;
      if (next && item.contains(next)) return;
      this._clearTimers();
      this._closeTimer = setTimeout(() => this._close(item), 80);
    }

    _onTriggerClick(e) {
      const trigger = e.currentTarget;
      const item = trigger.closest('[data-pf-mm-item]');
      if (!item) return;
      const isOpen = item.getAttribute("data-pf-mm-active") === "true";
      e.preventDefault();
      this._clearTimers();
      if (isOpen) this._close(item);
      else this._open(item);
    }

    _onKeyDown(e) {
      if (e.key === "Escape" && this._openItem) {
        e.preventDefault();
        const trigger = this._openItem.querySelector('[data-pf-mm-trigger]');
        this._close(this._openItem);
        if (trigger) trigger.focus();
        return;
      }
      const target = e.target;
      if (!target || !target.matches || !target.matches('[data-pf-mm-trigger]')) return;
      const item = target.closest('[data-pf-mm-item]');
      if (!item) return;
      const idx = this._items.indexOf(item);
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const next = this._items[(idx + 1) % this._items.length];
        next.querySelector('[data-pf-mm-trigger]').focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prev = this._items[(idx - 1 + this._items.length) % this._items.length];
        prev.querySelector('[data-pf-mm-trigger]').focus();
      } else if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        if (e.key === "ArrowDown") e.preventDefault();
        this._open(item);
        const firstLink = item.querySelector('[data-pf-mm-panel] a');
        if (firstLink && e.key === "ArrowDown") { e.preventDefault(); firstLink.focus(); }
      }
    }

    _onDocClick(e) {
      if (!this._openItem) return;
      if (this.contains(e.target)) return;
      this._closeAll(true);
    }
  }

  /* ---------- Mobile Drawer ---------- */
  class PFMobileMenu extends HTMLElement {
    constructor() {
      super();
      this._levelStack = [];
      this._scrollY = 0;
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onBackClick = this._onBackClick.bind(this);
      this._onOpenLevel = this._onOpenLevel.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this._backdrop = this.querySelector('[data-pf-mb-backdrop]');
      this._drawer = this.querySelector('[data-pf-mb-drawer]');
      this._levels = Array.from(this.querySelectorAll('[data-pf-mb-level]'));
      this._closeBtn = this.querySelector('[data-pf-mb-close]');
      this._isOpen = false;

      // Capture-phase listener — fires BEFORE halo's jQuery delegated handler, blocks it
      this._captureTriggerClick = (e) => {
        const trigger = e.target.closest('[data-pf-mb-trigger], .mobileMenu-toggle, [data-mobile-menu]');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (this._isOpen) this._close();
        else this._open();
      };
      document.addEventListener("click", this._captureTriggerClick, true);

      if (this._closeBtn) this._closeBtn.addEventListener("click", this._onCloseClick);
      if (this._backdrop) this._backdrop.addEventListener("click", this._onBackdropClick);

      this.querySelectorAll('[data-pf-mb-back]').forEach((b) => b.addEventListener("click", this._onBackClick));
      this.querySelectorAll('[data-pf-mb-open-level]').forEach((b) => b.addEventListener("click", this._onOpenLevel));

      // Close drawer when any link inside is clicked (Liquid renders <a> tags)
      this._onLinkClick = (e) => {
        const link = e.target.closest('a[href]');
        if (link && this._drawer && this._drawer.contains(link)) {
          // Allow navigation but reset drawer state immediately so it doesn't reappear after back-button
          this._close();
        }
      };
      if (this._drawer) this._drawer.addEventListener("click", this._onLinkClick);

      document.addEventListener("keydown", this._onKeyDown);

      // Defensive: pageshow (back/forward cache) — reset state
      this._onPageShow = () => {
        if (this._isOpen) this._close();
        document.body.classList.remove("pf-mm-open", "menu_open", "menu-open");
        document.body.style.top = "";
      };
      window.addEventListener("pageshow", this._onPageShow);

      const rootLevel = this.querySelector('[data-pf-mb-level="0"]');
      if (rootLevel) {
        rootLevel.setAttribute("data-pf-mb-state", "active");
        this._levelStack = [rootLevel];
      }
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this._onKeyDown);
      if (this._captureTriggerClick) document.removeEventListener("click", this._captureTriggerClick, true);
      if (this._onPageShow) window.removeEventListener("pageshow", this._onPageShow);
      if (this._haloKiller) { clearInterval(this._haloKiller); this._haloKiller = null; }
    }

    _clearHaloTimers() {
      if (this._haloKiller) { clearInterval(this._haloKiller); this._haloKiller = null; }
      if (this._haloKillerTimeout) { clearTimeout(this._haloKillerTimeout); this._haloKillerTimeout = null; }
    }

    _open() {
      if (this._isOpen) return;
      this._isOpen = true;
      this._previousFocus = document.activeElement; // save for focus restoration on close (WCAG 2.4.3)
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.classList.add("pf-mm-open");
      document.body.classList.remove("menu_open");
      document.body.classList.remove("menu-open");
      document.body.style.top = `-${this._scrollY}px`;
      if (this._drawer) {
        this._drawer.setAttribute("data-pf-mb-open", "true");
        this._drawer.removeAttribute("aria-hidden");
      }
      if (this._backdrop) this._backdrop.setAttribute("data-pf-mb-open", "true");
      // Kill any leftover halo overlay before we even start
      const overlays = document.querySelectorAll(".background-overlay");
      overlays.forEach((o) => { o.style.opacity = "0"; o.style.visibility = "hidden"; o.style.pointerEvents = "none"; });

      const firstFocus = this._drawer && (this._drawer.querySelector('[data-pf-mb-close]') || this._drawer.querySelector('input, button, a'));
      if (firstFocus) setTimeout(() => firstFocus.focus(), 50);

      // Defensive: kill halo's body classes if jQuery handler re-adds them
      this._clearHaloTimers();
      this._haloKiller = setInterval(() => {
        if (document.body.classList.contains("menu_open") || document.body.classList.contains("menu-open")) {
          document.body.classList.remove("menu_open");
          document.body.classList.remove("menu-open");
        }
      }, 50);
      this._haloKillerTimeout = setTimeout(() => this._clearHaloTimers(), 1500);
    }

    _close() {
      if (!this._isOpen) return;
      this._isOpen = false;
      if (this._drawer) {
        this._drawer.setAttribute("data-pf-mb-open", "false");
        this._drawer.setAttribute("aria-hidden", "true");
      }
      if (this._backdrop) this._backdrop.setAttribute("data-pf-mb-open", "false");
      document.body.classList.remove("pf-mm-open");
      document.body.classList.remove("menu_open");
      document.body.classList.remove("menu-open");
      // Strip any *-open state class halo may have applied alongside menu_open
      Array.from(document.body.classList).forEach((cls) => {
        if (/(^|[-_])(menu|sidebar|drawer|overlay)([-_])?open$/i.test(cls)) {
          document.body.classList.remove(cls);
        }
      });
      document.body.style.top = "";
      // Force reflow to ensure scroll restoration works reliably on iOS
      void document.body.offsetHeight;
      if (typeof this._scrollY === "number") window.scrollTo(0, this._scrollY);
      this._scrollY = 0;
      this._clearHaloTimers();
      // Aggressively force-hide any halo background overlay that may have been left visible
      const overlays = document.querySelectorAll(".background-overlay, .halo-sidebar.halo-sidebar_menu");
      overlays.forEach((o) => {
        o.style.opacity = "0";
        o.style.visibility = "hidden";
        o.style.pointerEvents = "none";
      });
      // Re-clear after the animation tail so the page is fully usable
      setTimeout(() => {
        document.body.classList.remove("pf-mm-open", "menu_open", "menu-open");
        overlays.forEach((o) => { o.style.opacity = "0"; o.style.visibility = "hidden"; o.style.pointerEvents = "none"; });
      }, 350);

      this._levels.forEach((lvl) => {
        if (lvl.getAttribute("data-pf-mb-level") === "0") lvl.setAttribute("data-pf-mb-state", "active");
        else lvl.removeAttribute("data-pf-mb-state");
      });
      const root = this.querySelector('[data-pf-mb-level="0"]');
      this._levelStack = root ? [root] : [];

      // Restore focus to the previously focused element (usually the hamburger trigger) — WCAG 2.4.3
      if (this._previousFocus && typeof this._previousFocus.focus === "function") {
        setTimeout(() => { try { this._previousFocus.focus(); } catch (err) {} this._previousFocus = null; }, 0);
      }
    }

    _onTriggerClick(e) {
      e.preventDefault();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      const isOpen = this._drawer && this._drawer.getAttribute("data-pf-mb-open") === "true";
      if (isOpen) this._close();
      else this._open();
    }

    _onCloseClick(e) {
      e.preventDefault();
      this._close();
    }

    _onBackdropClick(e) {
      e.preventDefault();
      this._close();
    }

    _onOpenLevel(e) {
      e.preventDefault();
      const target = e.currentTarget.getAttribute("data-pf-mb-open-level");
      const nextLevel = this.querySelector(`[data-pf-mb-level="${target}"]`);
      if (!nextLevel) return;
      const current = this._levelStack[this._levelStack.length - 1];
      if (current) current.setAttribute("data-pf-mb-state", "behind");
      nextLevel.setAttribute("data-pf-mb-state", "active");
      nextLevel.scrollTop = 0;
      this._levelStack.push(nextLevel);
      const back = nextLevel.querySelector('[data-pf-mb-back]');
      if (back) setTimeout(() => back.focus(), 100);
    }

    _onBackClick(e) {
      e.preventDefault();
      if (this._levelStack.length <= 1) return;
      const current = this._levelStack.pop();
      const previous = this._levelStack[this._levelStack.length - 1];
      if (current) current.removeAttribute("data-pf-mb-state");
      if (previous) previous.setAttribute("data-pf-mb-state", "active");
    }

    _onKeyDown(e) {
      const isOpen = this._drawer && this._drawer.getAttribute("data-pf-mb-open") === "true";
      if (!isOpen) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this._close();
      } else if (e.key === "Tab") {
        this._trapFocus(e);
      }
    }

    _trapFocus(e) {
      if (!this._drawer) return;
      const focusables = this._drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusables).filter((el) => {
        const lvl = el.closest('[data-pf-mb-level]');
        return !lvl || lvl.getAttribute("data-pf-mb-state") === "active" || lvl.getAttribute("data-pf-mb-level") === "0";
      });
      if (visible.length === 0) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- Register custom elements ---------- */
  if (!customElements.get("pf-mega-menu")) customElements.define("pf-mega-menu", PFMegaMenu);
  if (!customElements.get("pf-mobile-menu")) customElements.define("pf-mobile-menu", PFMobileMenu);
})();
